<script lang="ts" setup>
/**
 * リプライ情報 > いいね。
 */

import { inject, defineProps, reactive, toRefs } from "vue";
import { ApiPostLoadStatus, ApiPostLoadStatusType } from "@/modules/types/apiLoadStatusType";
import ApiPostLoadMaster from "@/components/common/api/ApiPostLoadMaster.vue";
import NiceController from "@/controllers/communityTalk/niceController";
import { CommunityTalkStore, isValidCommunityTalkStoreKey } from "@/store/communityTalk/communityTalkStore";
import CommunityTalkNice from "@/components/communityTalk/CommunityTalkNice.vue";
import PostPermissionUtil from "@/modules/utils/postPermissionUtil";
import LoginUtil from "@/modules/utils/loginUtil";

interface State {
	/**
	 * いいね送付時のAPIステータス。
	 */
	status: ApiPostLoadStatusType;
}

interface Props {
	/**
	 * いいね数。
	 */
	niceCount: number;
	/**
	 * リプライID。
	 */
	replyId: string;
	/**
	 * CommunityTalkStoreのInject用キー。
	 */
	communityTalkStoreKey: string;
}
const props = defineProps<Props>();

// reactiveな変数宣言
const state = reactive<State>({
	status: ApiPostLoadStatus.beforeRequest,
});
const { status } = toRefs(state);

// Storeの初期化処理
if (!isValidCommunityTalkStoreKey(props.communityTalkStoreKey)) {
	// なんらかの原因でInjectKeyにエラーが発生していた場合は強制終了
	throw new Error(`InjectKeyにエラーが発生しました。${props.communityTalkStoreKey.toString()}`);
}
const communityTalkStore = inject(props.communityTalkStoreKey) as CommunityTalkStore;

const wordSettings = communityTalkStore.getWordSettings();

/**
 * 投稿が許可されてるかどうか。
 */
const isPostable = PostPermissionUtil.isPostable(
	communityTalkStore.getGeneralSettings().nicePostPermissionSetting,
	communityTalkStore.getGeneralSettings().nicePostPermissionSpecificECMemberIds,
	communityTalkStore.getGeneralSettings().nonMemberIdList,
	communityTalkStore.getSiteUserId()
);

const niceController = new NiceController();
/**
 * いいね送付処理。
 */
const post = async (): Promise<void> => {
	try {
		let siteUserId = communityTalkStore.getSiteUserId();
		if (!LoginUtil.canSendSiteUserId(siteUserId, communityTalkStore.getGeneralSettings().nonMemberIdList, communityTalkStore.getGeneralSettings().nicePostPermissionSetting)) {
			siteUserId = undefined;
		}
		// いいね押下に対してローディングUIはくどいので、出さない
		// state.status = ApiPostLoadStatus.loading;
		// APIリクエスト
		await niceController.postReplyNice(communityTalkStore.getTenantId(), communityTalkStore.getTagCd(), props.replyId, communityTalkStore.getReviCoCookieId(), siteUserId);
		state.status = ApiPostLoadStatus.success;
	} catch (e) {
		// いいね送付時のエラーは、表示したとしてもエンドユーザーはどうしようもできない
		// このため、UI上では成功扱いで表示する
		state.status = ApiPostLoadStatus.success;
		console.error(e);
	}
};
</script>

<template>
	<api-post-load-master :status="status">
		<template #beforeRequest>
			<CommunityTalkNice :is-posted="false" :nice-count="niceCount" :is-postable="isPostable" @on-click-nice="post" :word-settings="wordSettings" />
		</template>
		<template #afterApiSuccess>
			<CommunityTalkNice :is-posted="true" :nice-count="niceCount" :is-postable="isPostable" :word-settings="wordSettings" />
		</template>
	</api-post-load-master>
</template>
